/**
 * edge-fetch — гонка мостов панели (18 авг 2026, приказ «работать безупречно»).
 *
 * Класс, который это закрывает: у панели два моста — прямой Амстердам
 * (panel.padla.pro) и RU-край (ru.panel.padla.pro, kubik→origin), и ТСПУ душит их
 * ВОЛНАМИ ПО ОДНОМУ (живой замер 18 авг: kubik→origin 69% потерь пакетов и
 * 0/10 крупных ответов при ИДЕАЛЬНОМ kubik→google; часом позже волна перекинулась
 * на путь клиент→kubik, а прямой ожил). Клиент был прибит к своему origin —
 * какой бы мост ни умер, у части юзеров панель «висела часами».
 *
 * Решение — ОДИН орган: перехват window.fetch только для /api-путей. Запрос идёт
 * активным мостом с таймаутом; сетевые смерть/таймаут у GET/HEAD → мгновенный
 * переезд на второй мост (успех делает его активным и липким через localStorage).
 * Мутации (POST/…) не ретраятся (дубль опаснее ожидания), но всегда ходят
 * актуальным живым мостом — и таймаут гонки к ним НЕ применяется (см.
 * MUTATION_TIMEOUT_MS: 8-секундный нож на долгих POST'ах давал ложные «не
 * отправлено» при реально доставленных директивах). 249 существующих fetch-мест
 * и все будущие обслуживаются без единой правки по коду; работает и в
 * iOS-webview (где Service Worker мёртв).
 *
 * HTTP-ошибки (4xx/5xx) — ЖИВОЙ мост, не повод для переезда: их обязан видеть
 * вызывающий как раньше. Переезд только на сетевом молчании.
 *
 * УМНЫЙ ВХОД (26 сен 2026, программа «Умный вход», строка 3.1). Страница по адресу умного входа
 * (`go.padla.pro`, `<код>.go.padla.pro`, закреплённый `<код>-ru|ams.go.padla.pro`) пришла через тот
 * мост, который наш DNS выбрал по пути телефона; какой — страница узнаёт из заголовка ответа
 * `X-Padla-Bridge: ru|ams` (его ставят сами мосты). Запасной мост — второй: у личного имени — то же
 * имя, закреплённое за другим мостом (`<код>-ams`/`<код>-ru`), у общего `go` — старый адрес второго
 * моста. Пока мост не назвался — запасной Амстердам напрямую (panel.padla.pro).
 */

export const PANEL_EDGES = ['https://ru.panel.padla.pro', 'https://panel.padla.pro'] as const;

/**
 * Адрес умного входа: `go.padla.pro`, `<код>.go.padla.pro`, `<код>-ru|ams.go.padla.pro` и имена мостов
 * `ru|ams.go.padla.pro`. Группы: 1 — код, 2 — мост закреплённого кода, 3 — имя моста.
 */
const GO_ORIGIN = /^https:\/\/(?:(?:([a-z0-9]{10})(?:-(ru|ams))?|(ru|ams))\.)?go\.padla\.pro$/;

/**
 * Имена мостов в нашей зоне (27 сен 2026): запасной путь при перескоке — свой, не старый адрес
 * `ru.panel.padla.pro` на чужом kubik (его сертификат кончается 6 окт; 27 сен на него ушла админка ректора).
 */
export const EDGE_NAMES = {
  ru: 'https://ru.go.padla.pro',
  ams: 'https://ams.go.padla.pro',
} as const;

export type BridgeId = 'ru' | 'ams';

/** Какой мост отдал страницу (заголовок `X-Padla-Bridge`); null — ещё не назвался. */
let servedBy: BridgeId | null = null;

/** Какой мост отдал страницу (заголовок `X-Padla-Bridge`); null — ещё не назвался или гонки нет. */
export function servedBridge(): BridgeId | null {
  return servedBy;
}

/** Мост, закреплённый в самом имени умного входа (`<код>-ru|ams`, `ru|ams.go`); null — имя не закреплено. */
export function pinnedBridgeOf(origin: string): BridgeId | null {
  const m = GO_ORIGIN.exec(origin);
  return ((m?.[2] ?? m?.[3]) as BridgeId | undefined) ?? null;
}

/** Адрес умного входа (любое имя под go)? */
export function isGoOrigin(origin: string): boolean {
  return GO_ORIGIN.test(origin);
}

/** Страница — на одном из мостов (старые адреса или умный вход)? */
export function isBridgeOrigin(origin: string): boolean {
  return (PANEL_EDGES as readonly string[]).includes(origin) || GO_ORIGIN.test(origin);
}

/**
 * Запасной мост для страницы на `origin`: личное имя — то же имя за другим мостом; общий `go`, имена мостов
 * и прямой Амстердам — имя другого моста в нашей зоне; старый `ru.panel` — Амстердам напрямую. Мост ещё не
 * назвался — Амстердам.
 */
export function fallbackEdgeFor(origin: string, bridge: BridgeId | null): string {
  if (origin === PANEL_EDGES[0]) return PANEL_EDGES[1];
  if (origin === PANEL_EDGES[1]) return EDGE_NAMES.ru;
  const m = GO_ORIGIN.exec(origin);
  const code = m?.[1];
  const current: BridgeId | null = ((m?.[2] ?? m?.[3]) as BridgeId | undefined) ?? bridge;
  if (current === null) return EDGE_NAMES.ams;
  const other: BridgeId = current === 'ru' ? 'ams' : 'ru';
  if (code) return `https://${code}-${other}.go.padla.pro`;
  return EDGE_NAMES[other];
}

const STORAGE_KEY = 'padla_edge_active';
/** Первичная попытка GET/HEAD: дольше — юзер смотрит на скелетон; короче — ложные переезды. */
const PRIMARY_TIMEOUT_MS = 8_000;
/** Второй мост — последний шанс, даём дышать глубже. */
const FALLBACK_TIMEOUT_MS = 20_000;
/**
 * Мутации (POST/PUT/PATCH/DELETE) в гонке мостов НЕ участвуют — им таймаут гонки
 * не положен ВООБЩЕ. Класс «директива не отправлена» (жалоба ректора 01.09.2026):
 * 8-секундный обрыв на POST /admin/creator-message убивал соединение, пока сервер
 * дописывал LLM-ответ (7–24 с по ledger), — сервер доводил отправку до конца, а
 * панель врала «сеть оборвалась». Обрыв мутации не даёт НИЧЕГО (ретрая нет), но
 * создаёт ложный провал — худшее из обоих миров. Потолок здесь — только страховка
 * от истинного блэкхола (ТСПУ-молчание): 70 с > 60 с собственного таймаута
 * mobile-gw на длинных ручках (AbortSignal.timeout(60000) в admin-users.ts) —
 * честный ответ сервера (в т.ч. его 504) всегда успевает раньше нашего ножа.
 */
const MUTATION_TIMEOUT_MS = 70_000;

type FetchLike = typeof fetch;

let activeEdge: string | null = null;

function readSticky(origin: string): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && (v === origin || v === fallbackEdgeFor(origin, servedBy)) ? v : null;
  } catch {
    return null;
  }
}

function persistSticky(edge: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, edge);
  } catch {
    /* приватный режим — липкость сессионная, не смертельно */
  }
}

/** Активный мост (липкий). Экспорт — для диагностики и тестов. */
export function getActiveEdge(origin: string): string {
  if (activeEdge) return activeEdge;
  activeEdge = readSticky(origin) ?? (isBridgeOrigin(origin) ? origin : PANEL_EDGES[1]);
  return activeEdge;
}

function setActiveEdge(edge: string): void {
  activeEdge = edge;
  persistSticky(edge);
}

function otherEdge(edge: string, origin: string): string {
  return edge === origin ? fallbackEdgeFor(origin, servedBy) : origin;
}

/** /api-запрос к нам? (относительный или абсолютный на любой из мостов). */
export function isEdgeApiUrl(url: URL, origin: string): boolean {
  if (!url.pathname.startsWith('/api/')) return false;
  return (
    url.origin === origin ||
    (PANEL_EDGES as readonly string[]).includes(url.origin) ||
    url.origin === fallbackEdgeFor(origin, servedBy)
  );
}

/** Запомнить, какой мост отдаёт страницу (только ответы её собственного адреса). */
function learnBridge(resp: Response): void {
  const v = resp.headers?.get?.('x-padla-bridge');
  if (v === 'ru' || v === 'ams') servedBy = v;
}

function withEdge(url: URL, edge: string): string {
  return edge + url.pathname + url.search;
}

/** Сигнал вызывающего ⊕ наш таймаут (AbortSignal.any с ручным фолбэком). */
function combineSignal(
  caller: AbortSignal | null | undefined,
  timeoutMs: number
): { signal: AbortSignal; isOurTimeout: () => boolean; cleanup: () => void } {
  const ctrl = new AbortController();
  let ourTimeout = false;
  const timer = setTimeout(() => {
    ourTimeout = true;
    ctrl.abort();
  }, timeoutMs);
  const onCallerAbort = (): void => ctrl.abort();
  if (caller) {
    if (caller.aborted) ctrl.abort();
    else caller.addEventListener('abort', onCallerAbort, { once: true });
  }
  return {
    signal: ctrl.signal,
    isOurTimeout: () => ourTimeout,
    cleanup: () => {
      clearTimeout(timer);
      caller?.removeEventListener('abort', onCallerAbort);
    },
  };
}

export interface EdgeFetchDeps {
  baseFetch: FetchLike;
  origin: string;
}

/** Обёрнутый fetch: гонка мостов для /api, прозрачный пропуск остального. */
export function makeEdgeFetch(deps: EdgeFetchDeps): FetchLike {
  const { baseFetch, origin } = deps;
  return async function edgeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    let url: URL;
    try {
      url = new URL(
        typeof input === 'string' || input instanceof URL ? String(input) : input.url,
        origin
      );
    } catch {
      return baseFetch(input as RequestInfo, init);
    }
    if (!isEdgeApiUrl(url, origin)) return baseFetch(input as RequestInfo, init);

    const method = (
      init?.method ??
      (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
    ).toUpperCase();
    const callerSignal =
      init?.signal ??
      (typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined);
    const retriable = method === 'GET' || method === 'HEAD';

    const attempt = async (edge: string, timeoutMs: number): Promise<Response> => {
      const { signal, isOurTimeout, cleanup } = combineSignal(callerSignal, timeoutMs);
      try {
        return await baseFetch(withEdge(url, edge), { ...init, signal });
      } catch (e) {
        // Различаем: наш таймаут / сетевую смерть — кандидаты на переезд;
        // отмену вызывающего — пробрасываем как есть (юзер ушёл с экрана).
        if (callerSignal?.aborted && !isOurTimeout()) throw e;
        throw Object.assign(e instanceof Error ? e : new Error(String(e)), {
          __edgeDead: true,
        });
      } finally {
        cleanup();
      }
    };

    const first = getActiveEdge(origin);
    try {
      const resp = await attempt(first, retriable ? PRIMARY_TIMEOUT_MS : MUTATION_TIMEOUT_MS);
      if (first === origin) learnBridge(resp);
      return resp;
    } catch (e) {
      const dead = (e as { __edgeDead?: boolean }).__edgeDead === true;
      if (!dead || !retriable) throw e;
      const second = otherEdge(first, origin);
      const resp = await attempt(second, FALLBACK_TIMEOUT_MS);
      setActiveEdge(second); // мост ответил — он теперь активный для всех запросов
      return resp;
    }
  };
}

/** Сброс модульного состояния (только тесты). */
export function __resetEdgeStateForTests(): void {
  activeEdge = null;
  servedBy = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* jsdom без storage */
  }
}

/**
 * Гонка мостов — только внутри Telegram (мини-апп). В обычном браузере панель — это админка (вход ключом,
 * сессия-кука своего адреса): страница пришла напрямую, а через мост шлюз админку не пускает (`via_bridge`,
 * TLS моста снимает чужая машина) и кука на чужом адресе не действует. 27 сен 2026: шлюз перезапускался
 * секунды — браузер ректора переехал на ru.panel (kubik), запомнил его, и вход в админку стал «сервер не
 * ответил» (403 via_bridge). Признак мини-аппа — подписанные данные Telegram (initData); в браузере их нет.
 */
export function insideTelegram(w: { Telegram?: { WebApp?: { initData?: unknown } } }): boolean {
  const data = w.Telegram?.WebApp?.initData;
  return typeof data === 'string' && data.length > 0;
}

/** Поставить перехват. Вне прод-мостов (localhost/dev/стенды) и вне Telegram (браузер — админка) — не вмешиваемся. */
export function installEdgeFetch(): void {
  if (typeof window === 'undefined') return;
  const origin = window.location.origin;
  if (!isBridgeOrigin(origin)) return;
  if (!insideTelegram(window as unknown as { Telegram?: { WebApp?: { initData?: unknown } } }))
    return;
  const base = window.fetch.bind(window);
  window.fetch = makeEdgeFetch({ baseFetch: base, origin });
}
